import {
    ActivatedRouteSnapshot,
    DetachedRouteHandle,
    RouteReuseStrategy,
} from '@angular/router';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class CustomReuseStrategy implements RouteReuseStrategy {
    readonly #storedHandles = new Map<string, DetachedRouteHandle>();

    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        return this.#isCacheable(route);
    }

    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        if (!this.#isCacheable(route)) return;
        const key = this.#getRouteKey(route);
        this.#storedHandles.set(key, handle);
    }

    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (!this.#isCacheable(route)) return false;
        const key = this.#getRouteKey(route);
        const has = this.#storedHandles.has(key);
        return has;
    }

    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        if (!this.#isCacheable(route)) return null;
        const key = this.#getRouteKey(route);
        return this.#storedHandles.get(key) ?? null;
    }

    shouldReuseRoute(
        future: ActivatedRouteSnapshot,
        current: ActivatedRouteSnapshot,
    ): boolean {
        return future.routeConfig === current.routeConfig;
    }

    #getRouteKey(route: ActivatedRouteSnapshot): string {
        const segments: string[] = [];
        const visited = new Set<ActivatedRouteSnapshot>();
        let current: ActivatedRouteSnapshot | null = route;

        while (current && !visited.has(current)) {
            visited.add(current);
            const configPath = current.routeConfig?.path;
            if (configPath) {
                let segment = configPath;
                for (const [key, value] of Object.entries(current.params)) {
                    segment = segment.replace(`:${key}`, value);
                }
                segments.unshift(segment);
            }
            current = current.parent;
        }
        return segments.join('/');
    }

    #isCacheable(route: ActivatedRouteSnapshot): boolean {
        const key = this.#getRouteKey(route);
        const isLeafRoute = /^comms\/(voice)\/[^/]+$/.test(key);
        const isPrimary = route.outlet === 'primary';
        const isPotentialParent =
            !!route.routeConfig?.redirectTo ||
            !!route.routeConfig?.children ||
            !!route.firstChild;

        const safeToCache =
            isLeafRoute &&
            isPrimary &&
            !isPotentialParent &&
            key !== 'comms/voice/new';
        return safeToCache;
    }

    clearCacheFor(pathKey: string): void {
        if (this.#storedHandles.has(pathKey)) {
            this.#storedHandles.delete(pathKey);
        }
    }

    clearAll(): void {
        this.#storedHandles.clear();
    }
}
